import { FileSignature } from 'lucide-react'
import { useMemo } from 'react'
import Box from '@/common/components/Box'
import ResumeItem from '@/common/components/ResumeItem'
import { cn } from '@/common/utils/cn'
import { formatDateInLocalTimezone } from '@/common/utils/formatters'
import {
  MaintenanceForwardingSectionStyle,
  maintenanceForwardingSectionStyles,
} from '@/features/maintenance/features/workflows/constants/forwardings/maintenanceForwardingSectionStyles.constant'
import { proposalDirectiveEntries } from '../../../constants/proposalDirectiveEntries.constant'
import { ManualEntryProposalShowData } from '../../../types/maintenanceManualEntryProposal.type'
import ProposalAddProposalButton from '../../../components/ProposalAddProposalButton'
import { ColumnHeader, Section } from './showPrimitives'

const date = (value?: string | null) => (value ? formatDateInLocalTimezone(value.slice(0, 10)) : '--')

type Props = {
  proposal: ManualEntryProposalShowData
  onAccepted?: () => void
}

const ProposalEstablishmentColumn = ({ proposal, onAccepted }: Props) => {
  const forwarding = proposal.manual_entry?.forwarding
  const directiveEntryType = forwarding?.directive_entry_type
  const directiveConfig = directiveEntryType ? proposalDirectiveEntries[directiveEntryType] : undefined
  const content = proposal.proposal_content
  const awaitingEstablishment = proposal.status === 'awaiting_establishment_response'
  const hasSentProposal = !!content || !!proposal.establishment_accepted_at

  const sectionStyle = useMemo(() => {
    if (proposal.status === 'completed') {
      return maintenanceForwardingSectionStyles[MaintenanceForwardingSectionStyle.Completed]
    }

    if (awaitingEstablishment) {
      return maintenanceForwardingSectionStyles[MaintenanceForwardingSectionStyle.Pending]
    }

    if (proposal.status === 'awaiting_client_response') {
      return maintenanceForwardingSectionStyles[MaintenanceForwardingSectionStyle.Waiting]
    }

    return maintenanceForwardingSectionStyles[MaintenanceForwardingSectionStyle.Default]
  }, [proposal.status, awaitingEstablishment])

  return (
    <div className="flex flex-col gap-4">
      <ColumnHeader icon={FileSignature} title="Proposta do estabelecimento" />

      {!hasSentProposal ? (
        <Box className={cn('flex flex-col items-center justify-center gap-3 p-10 text-center', sectionStyle)}>
          <FileSignature className="size-10 text-white-300" strokeWidth={1.5} />
          <div className="flex flex-col gap-1">
            <p className="text-sm font-bold text-white-800">
              {awaitingEstablishment ? 'Nenhuma proposta enviada ainda' : 'Nenhuma proposta foi enviada'}
            </p>
            <p className="text-xs text-white-400">
              {awaitingEstablishment
                ? 'Adicione uma proposta com os valores para enviar ao cliente.'
                : 'Este processo foi encerrado sem o envio de uma proposta.'}
            </p>
          </div>
          {awaitingEstablishment && <ProposalAddProposalButton proposal={proposal} onAccepted={onAccepted} />}
        </Box>
      ) : (
        <Section title="Proposta enviada" className={sectionStyle}>
          <div className="flex flex-wrap gap-x-8 gap-y-3">
            <ResumeItem className="text-xs" valueClassName="font-bold text-sm" label="Início sugerido" value={date(content?.start_at)} />
            <ResumeItem
              className="text-xs"
              valueClassName="font-bold text-sm"
              label="Prazo de conclusão"
              value={content?.deadline_to_complete_in_days != null ? `${content.deadline_to_complete_in_days} dia(s)` : '--'}
            />
            <ResumeItem className="text-xs" valueClassName="font-bold text-sm" label="Previsão de conclusão" value={date(content?.estimated_completion_date)} />
          </div>

          {directiveConfig?.SentProposalExtras && <directiveConfig.SentProposalExtras proposal={proposal} />}
        </Section>
      )}
    </div>
  )
}

export default ProposalEstablishmentColumn
